import { FC, useContext, useEffect, useState } from "react";
import { isPort } from "validator";

import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import validIp from "../validators/ip";
import PingClusterResult from "./PingClusterResult";
import SaveButton from "./SaveButton";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";
import usePingCluster from "@/hooks/use-ping-cluster";
import { useGetSettingsQuery } from "@/store/enhancedApi";
import { Cluster } from "@/store/pomeriumApi";
import PeriodicTask from "@/utils/PeriodicTask";

type PingClusterError = {
  code: string;
  message: string;
};

type ClusterStatusProperties = {
  organizationId?: string;
  cluster?: Cluster;

  setAddressAndPort: (
    address: string | undefined,
    port: string | undefined,
  ) => void;
};

export const ClusterStatus: FC<ClusterStatusProperties> = ({
  organizationId,
  cluster,
  setAddressAndPort,
}) => {
  const { user } = useContext(UserContext);
  const { data: settings } = useGetSettingsQuery(
    {
      organizationId: organizationId || "",
      clusterId: cluster?.id || "",
    },
    {
      skip: !organizationId || !cluster?.id,
      clusterFlavor: cluster?.flavor,
    },
  );
  const [pingCluster, pingClusterResult] = usePingCluster();
  const clusterIp =
    cluster?.manualOverrideIpAddress || cluster?.autoDetectIpAddress;

  const [showForm, setShowForm] = useState(false);
  const [manualIp, setManualIp] = useState("");
  const [originalManualIp, setOriginalManualIp] = useState("");
  const [customPort, setCustomPort] = useState("");
  const [originalCustomPort, setOriginalCustomPort] = useState("");
  const [manualIpError, setManualIpError] = useState("");
  const [customPortError, setCustomPortError] = useState("");

  const [pingClusterError, setPingClusterError] = useState<
    PingClusterError | undefined
  >(undefined);

  useEffect(() => {
    const v = cluster?.manualOverrideIpAddress || "";
    setManualIp(v);
    setOriginalManualIp(v);
  }, [cluster?.manualOverrideIpAddress]);

  useEffect(() => {
    const v = settings?.address?.split(":")?.pop() || "443";
    setCustomPort(v);
    setOriginalCustomPort(v);
  }, [settings?.address]);

  const [pingTask, setPingTask] = useState<PeriodicTask<any> | null>(null);
  useEffect(() => {
    const t = new PeriodicTask(5000);
    setPingTask(t);
    return () => t.cancel();
  }, []);
  useEffect(() => {
    if (pingTask && organizationId && cluster?.id && clusterIp) {
      pingTask.run(() => pingCluster(organizationId, cluster));
    }
  }, [pingTask, organizationId, cluster?.id, clusterIp, settings?.address]);

  useEffect(() => {
    if (pingClusterResult?.errorCode || pingClusterResult?.errorMessage) {
      // if there's an error code, show the form
      setShowForm(true);
    } else if (customPort === "443" && !manualIp) {
      // if there's no error code, and we're using the default settings, hide the form
      setShowForm(false);
    }

    if (pingClusterResult?.success) {
      sendStandardEvent("Pomerium Core Connected", "onboarding", user);
      setPingClusterError(undefined);
    } else if (pingClusterResult?.errorCode) {
      const errorData = {
        code: pingClusterResult.errorCode,
        message: pingClusterResult.errorMessage || "",
      };
      sendStandardEvent(
        "Pomerium Core Connection Error",
        "onboarding",
        user,
        errorData,
      );
      setPingClusterError(errorData);
    }
  }, [
    pingClusterResult?.success,
    pingClusterResult?.errorCode,
    pingClusterResult?.errorMessage,
  ]);

  useEffect(() => {
    setCustomPortError(
      isPort(customPort)
        ? ""
        : "Please input a valid port. Examples: 443 | 5000",
    );
  }, [customPort]);
  useEffect(() => {
    setManualIpError(validIp(manualIp));
  }, [manualIp]);

  const handleApply = () => {
    setOriginalManualIp(manualIp);
    setOriginalCustomPort(customPort);
    setAddressAndPort(
      manualIp || undefined,
      customPort === "443" ? undefined : customPort,
    );
  };

  return (
    <>
      <Stack
        direction="column"
        spacing={1}
        justifyContent="space-evenly"
        className="border rounded-lg"
      >
        {!!cluster?.importStatus &&
          !cluster?.importStatus.error &&
          !cluster?.importStatus?.warnings && (
            <Alert severity="success">
              <AlertTitle>Import successful</AlertTitle>
              {cluster.onboardingStatus === "in_progress" &&
                "Your new cluster will use the imported configuration when it connects."}
              {cluster.onboardingStatus === "completed" &&
                "Your new cluster is using the imported configuration."}
            </Alert>
          )}
        {!!cluster?.importStatus &&
          !cluster?.importStatus.error &&
          !!cluster?.importStatus?.warnings && (
            <Alert severity="info" color="warning" variant="outlined">
              <AlertTitle>
                Import succeeded with {cluster.importStatus.warnings.length}{" "}
                warning{cluster.importStatus.warnings.length != 1 && "s"}
              </AlertTitle>
              {cluster.importStatus.warnings.map((warning) => (
                <>
                  • {warning}
                  <br />
                </>
              ))}
              {cluster.onboardingStatus === "in_progress" && (
                <>
                  <br />
                  Your new cluster will use the imported configuration when it
                  connects.
                </>
              )}
              {cluster.onboardingStatus === "completed" && (
                <>
                  <br />
                  Your new cluster is using the imported configuration.
                </>
              )}
              <br />
              If you have questions please contact{" "}
              <a
                href={
                  "mailto:support@pomerium.com?subject=Warning when importing Cluster: " +
                  cluster?.id
                }
              >
                support
              </a>
              .
            </Alert>
          )}
        {cluster?.importStatus?.error && (
          <Alert severity="error">
            <AlertTitle>Import failed</AlertTitle>
            {cluster.importStatus.error}
            <br />
            If you have questions please contact{" "}
            <a
              href={
                "mailto:support@pomerium.com?subject=Error when importing Cluster: " +
                cluster?.id
              }
            >
              support
            </a>
            .
          </Alert>
        )}

        <PingClusterResult
          result={pingClusterResult}
          error={pingClusterError}
          clusterIp={clusterIp}
          customPort={customPort}
        />

        {showForm && (
          <Box>
            <Typography variant="body1">
              To use a custom IP address for this cluster enter it here:
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. 127.0.0.1"
              value={manualIp}
              onChange={(evt) => setManualIp(evt.target.value)}
              error={!!manualIpError}
              helperText={manualIpError}
            />
            <Typography variant="body1">
              To use a custom listening port for this cluster enter it here:
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                placeholder="e.g. 8443"
                value={customPort}
                onChange={(evt) => setCustomPort(evt.target.value)}
                error={!!customPortError}
                helperText={customPortError}
              />
              <SaveButton
                variant="contained"
                size="small"
                disabled={
                  customPort === originalCustomPort &&
                  manualIp === originalManualIp
                }
                onClick={handleApply}
              >
                Apply
              </SaveButton>
            </Stack>
          </Box>
        )}
      </Stack>
    </>
  );
};
export default ClusterStatus;
